import { apiDelete, apiGet, apiPatch, apiPost } from './client';

export type AiConfigurationStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type AiExecutionMode = 'AUTOMATIC' | 'SUPERVISED' | 'HUMAN';

export interface AiKnowledgeRule {
  id: string;
  name: string;
  matchType: 'INSTALLATION_SCHEDULE_EXCEPTION' | 'INSTALLATION_DEADLINE' | 'INSTALLATION_PRICE' | 'INSTALLATION_FREE' | 'ROUTER' | 'LOYALTY' | 'SUPPORT' | 'COVERAGE' | 'BENEFITS' | 'CUSTOM';
  matchDescription: string;
  triggerTerms: string[];
  patterns: string[];
  instruction: string;
  safeResponse: string;
  active: boolean;
}

export interface AiKnowledgeItem {
  id: string;
  title: string;
  category: string;
  content: string;
  rules: AiKnowledgeRule[] | null;
  source: string | null;
  visibility: 'CUSTOMER' | 'INTERNAL';
  reviewStatus: 'DRAFT' | 'REVIEWED';
  confidence: number;
  validFrom: string | null;
  validUntil: string | null;
  updatedAt: string;
}

export interface AiServicePlan {
  id: string;
  name: string;
  sgpPlanId: string | null;
  speedMbps: number | null;
  price: number;
  installationFee: number | null;
  loyaltyMonths: number | null;
  benefits: string[] | null;
  coverageNotes: string | null;
  eligibilityRules: Record<string, unknown> | null;
  isActive: boolean;
}

export interface AiServiceFlow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  department: string | null;
  requiredFields: Array<Record<string, unknown>>;
  steps: Array<Record<string, unknown>>;
  fallbackRules: Record<string, unknown> | null;
  isActive: boolean;
}

export interface SgpOperationDefinition {
  id: string;
  module: string;
  name: string;
  risk: 'READ' | 'SAFE_WRITE' | 'SENSITIVE_WRITE' | 'CRITICAL';
  status: 'IMPLEMENTED' | 'PLANNED';
  automaticAllowed: boolean;
  requiresCustomerConfirmation: boolean;
  requiresHumanApproval: boolean;
}

export interface AiOperationPolicy {
  id: string;
  operationId: string;
  enabled: boolean;
  executionMode: AiExecutionMode;
  requiresCustomerConfirmation: boolean;
  requiresHumanApproval: boolean;
  operation: SgpOperationDefinition | null;
}

export interface AiValidationReport {
  valid: boolean;
  errors: string[];
  warnings: string[];
  checkedAt: string;
  counts: { knowledge: number; reviewedKnowledge: number; plans: number; flows: number; enabledOperations: number };
}

export interface AiConfiguration {
  id: string;
  companyId: string;
  version: number;
  status: AiConfigurationStatus;
  assistantName: string;
  tone: string;
  businessContext: string | null;
  instructions: string | null;
  responseRules: Record<string, unknown> | null;
  memoryEnabled: boolean;
  confidenceThreshold: number;
  memoryPolicy: Record<string, unknown> | null;
  autonomyPolicy: Record<string, unknown> | null;
  validationReport: AiValidationReport | null;
  publishedAt: string | null;
  updatedAt: string;
  knowledgeItems: AiKnowledgeItem[];
  servicePlans: AiServicePlan[];
  flows: AiServiceFlow[];
  operationPolicies: AiOperationPolicy[];
}

export interface AiWorkspace {
  company: { id: string; name: string };
  draft: AiConfiguration;
  published: AiConfiguration | null;
  catalog: SgpOperationDefinition[];
}

export const getAiWorkspace = (companyId: string) =>
  apiGet<AiWorkspace>(`/ai-configurations/workspace?companyId=${encodeURIComponent(companyId)}`);

export const getSdrSources = () =>
  apiGet<string[]>('/ai-configurations/sdr-sources');

export const updateAiIdentity = (id: string, data: Record<string, unknown>) =>
  apiPatch<AiConfiguration>(`/ai-configurations/${id}/identity`, data);

export const updateAiSettings = (id: string, data: Record<string, unknown>) =>
  apiPatch<AiConfiguration>(`/ai-configurations/${id}/settings`, data);

export const createAiKnowledge = (id: string, data: Record<string, unknown>) =>
  apiPost<AiKnowledgeItem>(`/ai-configurations/${id}/knowledge`, data);

export const updateAiKnowledge = (id: string, itemId: string, data: Record<string, unknown>) =>
  apiPatch<AiKnowledgeItem>(`/ai-configurations/${id}/knowledge/${itemId}`, data);

export const deleteAiKnowledge = (id: string, itemId: string) =>
  apiDelete<{ deleted: boolean }>(`/ai-configurations/${id}/knowledge/${itemId}`);

export const createAiPlan = (id: string, data: Record<string, unknown>) =>
  apiPost<AiServicePlan>(`/ai-configurations/${id}/plans`, data);

export const updateAiPlan = (id: string, itemId: string, data: Record<string, unknown>) =>
  apiPatch<AiServicePlan>(`/ai-configurations/${id}/plans/${itemId}`, data);

export const deleteAiPlan = (id: string, itemId: string) =>
  apiDelete<{ deleted: boolean }>(`/ai-configurations/${id}/plans/${itemId}`);

export const createAiFlow = (id: string, data: Record<string, unknown>) =>
  apiPost<AiServiceFlow>(`/ai-configurations/${id}/flows`, data);

export const updateAiFlow = (id: string, itemId: string, data: Record<string, unknown>) =>
  apiPatch<AiServiceFlow>(`/ai-configurations/${id}/flows/${itemId}`, data);

export const deleteAiFlow = (id: string, itemId: string) =>
  apiDelete<{ deleted: boolean }>(`/ai-configurations/${id}/flows/${itemId}`);

export const updateAiOperation = (id: string, operationId: string, data: Record<string, unknown>) =>
  apiPatch<AiOperationPolicy>(`/ai-configurations/${id}/operations/${encodeURIComponent(operationId)}`, data);

export const validateAiConfiguration = (id: string) =>
  apiPost<AiValidationReport>(`/ai-configurations/${id}/validate`);

export const publishAiConfiguration = (id: string) =>
  apiPost<AiConfiguration>(`/ai-configurations/${id}/publish`);
